import DataGridViewModel from '@/ViewModels/Controls/DataGridViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import RADApplication from '@/Management/RADApplication';
import Loader from '@/Utility/Loader';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import * as CoreFrameDTOs from "coreframetypings/dto";
import Utilities from '@/Utility/Utilities';
import { GridColumn } from '#/DefaultViewExtensionInterfaces';
import { ISearchQueryDTO, ISearchGridDTO } from "coreframetypings/dto";
import { ISearchGridViewModelParameters } from '#/DefaultViewExtensionInterfaces';
import User from '@/Model/User';

export default class SearchGridViewModel extends DataGridViewModel {

    private timeoutId: number;

    /**
     * Speichert die zuletzt gesuchte Query um entscheiden zu können, welche Operation aufgerufen werden muss
     */
    public lastSearchQuery: KnockoutObservable<CoreFrameDTOs.ISearchQueryDTO>;

    /**
     * Das aktuelle SearchQueryDTO
     */
    public searchQuery: KnockoutObservable<CoreFrameDTOs.ISearchQueryDTO>;

    public showCreated : boolean;

    public showHistorizationColumn: KnockoutObservable<boolean>;

    /**
     * Das ViewModel für die SearchGrid
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    public constructor(protected params: ISearchGridViewModelParameters) {
        super($.extend(params,
            {
                autoInit: false
            }));

        this.showCreated = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.Search.ShowCreatedAndChangedBy');
        this.extentVisible = this.paramOrDefault(params.extentVisible, true);
        this.lastSearchQuery = this.observableOrDefault(null);
        this.isMultiColumnSelect = this.observableOrDefault(params.isMultiColumnSelect);
        this.searchQuery = this.observableOrDefault(params.searchQuery, null);
        this.addPdoIdColumnByDefault = false;
        this.showHistorizationColumn = this.observableOrDefault(false);
        this.hovered.extend({ notify: 'always' });

        this.defineSubscription(this.showHistorizationColumn, () => {
            this.columnDefinition(this.getColumnDefinition());
        });

        this.columnDefinition(this.getColumnDefinition());
    }

    private getColumnDefinition() {
        return [
            {
                name: 'PDO_ID',
                field: 'pdoId',
                ergName: 'PDO_ID',
                unitNames: [],
                sortable: true,
                isFilterable: false,
                isVisible: User.currentUserShouldShowPDOID()
            },
            {
                name: 'ObjectIdent',
                field: 'objectIdentValue',
                ergName: 'SEARCH_RESULT',
                unitNames: [],
                sortable: true,
                isFilterable: false,
                isVisible: true
            },
            {
                name: 'Type',
                field: 'typeErgName',
                ergName: 'SEARCH_OBJECT_TYPE',
                unitNames: [],
                sortable: true,
                isFilterable: false,
                isVisible: true
            },
            {
                name: 'TypeName',
                field: 'typeName',
                ergName: 'SEARCH_OBJECT_TYPE',
                unitNames: [],
                sortable: false,
                isFilterable: false,
                isVisible: false
            },
            {
                name: 'DataHistoryDate',
                field: 'historizationDate',
                ergName: 'SEARCH_HISTORY_DATE',
                unitNames: [],
                sortable: true,
                isFilterable: false,
                isVisible: this.showHistorizationColumn(),
                listViewColumnFactory: 'DateTimeColumn?format=Date'
            },
            {
                name: 'Created',
                field: 'pdOCreated',
                ergName: 'SEARCH_CREATED_ON',
                unitNames: [],
                sortable: true,
                isFilterable: false,
                isVisible: true,
                listViewColumnFactory: 'DateTimeColumn'
            },
            {
                name: 'CreatedBy',
                field: 'pdOCreatedBy',
                ergName: 'SEARCH_CREATED_BY',
                unitNames: [],
                sortable: true,
                isFilterable: false,
                isVisible: this.showCreated
            },
            {
                name: 'Changed',
                field: 'pdOChanged',
                ergName: 'SEARCH_CHANGED_ON',
                unitNames: [],
                sortable: true,
                isFilterable: false,
                isVisible: true,
                listViewColumnFactory: 'DateTimeColumn'
            },
            {
                name: 'ChangedBy',
                field: 'pdOChangedBy',
                ergName: 'SEARCH_CHANGED_BY',
                unitNames: [],
                sortable: true,
                isFilterable: false,
                isVisible: this.showCreated
            },
            {
                name: 'Principal',
                field: 'principal',
                ergName: 'PRINCIPAL_NAME',
                unitNames: [],
                sortable: true,
                isFilterable: false,
                isVisible: true
            }
        ];
    }

    /**
     * @override 
     * @inheritdoc 
     */
    protected refreshGrid(): JQueryPromise<any> {
        let page = this.page();
        let pageSize = this.pageSize();
        let sort = this.orderBy();

        let searchQuery = this.searchQuery();
        let sortField = this.getSortField();
        let reverseSort = this.getReverseSort();
        
        if (searchQuery && searchQuery.includeHistory)
            this.showHistorizationColumn(true);
        else
            this.showHistorizationColumn(false);
        let columns = this.columns();
        let data = [];
        
        if (searchQuery != null) {
            if (sortField !== searchQuery.sortField) {
                searchQuery.sortField = sortField;
            }

            if (reverseSort !== searchQuery.reverseSort) {
                searchQuery.reverseSort = reverseSort;
            }

            searchQuery.pageSize = pageSize;

            Loader.getInstance().show();

            if (JSON.stringify(searchQuery) !== JSON.stringify(this.lastSearchQuery())) {
                // Das generierte DTO kann auf Serverseite nicht richtig gemappt werden (CF-92916). Deshalb helfen wir ein wenig nach.
                const queryForServer: ISearchQueryDTO | {
                    HistorizationPointPDO_ID: string;
                    selectedType: {
                        PDO_ID: string
                    }
                } = {
                    ...searchQuery,
                    HistorizationPointPDO_ID: searchQuery.historizationPointPDOID,
                    selectedType: searchQuery.selectedType != null ? {
                        PDO_ID: searchQuery.selectedType.pdoId
                    } : null
                };
                
                return RADEnvironmentProxy.getInstance().search({ // Dies startet eine Suche, die auf jeden Fall bei Seite 1 beginnt
                    query: queryForServer as any
                }).then((searchResultDTO) => {
                    if (this.isDisposed) {
                        return;
                    }

                    data = this.getSearchGridExtent(searchResultDTO.entries, columns);
                    this.lastSearchQuery($.extend(true, {}, searchQuery));

                    this.page(1);
                    this.totalRows(searchResultDTO.extentCount);
                    this.realTotalRows(searchResultDTO.extentCount);
                    this.data(data);
                }).always(() => {
                    if (!this.isDisposed)
                        this.isBusy(false);
                    Loader.getInstance().hide();
                }).promise();
            } else {
                let start = (page - 1) * pageSize;
                let end = Math.max(0, start + pageSize - 1);

                if (!Utilities.isInt(start) || start < 0) start = 0;
                if (!Utilities.isInt(end) || end < 0) end = 0;

                return RADEnvironmentProxy.getInstance().getSearchExtent({
                    start: start,
                    end: end
                }).then((searchResultDTO) => {
                    if (this.isDisposed) {
                        return;
                    }

                    data = this.getSearchGridExtent(searchResultDTO.entries, columns);

                    this.totalRows(searchResultDTO.extentCount);
                    this.realTotalRows(searchResultDTO.extentCount);
                    this.data(data);
                }).always(() => {
                    if (!this.isDisposed)
                        this.isBusy(false);
                    Loader.getInstance().hide();
                }).promise();
            }
        } else {
            this.isBusy(false);
        }

        // Default
        let deferred = $.Deferred();
        return deferred.resolve().promise();
    }

    /**
     * Liefert Suchergebnisse
     */
    private getSearchGridExtent(entries: CoreFrameDTOs.ISearchEntryDTO[], columns: GridColumn[]) {
        const data: any[] = [];
        let fields = _.map(columns, col => col.field);
        // Diese beiden Felder müssen immer mit übertragen werden
        fields.push("isHistorized");
        fields.push("historizationGroupPDO_ID");

        for (let i = 0; i < entries.length; i++) {
            let generic: Partial<CoreFrameDTOs.ISearchEntryDTO> = {};
            let result: any = {};
            let index = 0;

            for (let j = 0; j < fields.length; j++) {
                let field = fields[j];
                generic[field] = entries[i][field];
                index++;
            }

            for (let j = 0; j < fields.length; j++) {
                let field = fields[j];

                if (field === 'typeErgName') {
                    let resolvedName = ResourceManager.getInstance().get(generic[field]);

                    result[field] = resolvedName;
                } else if (field == 'typeName') {
                    result["pdTypeName"] = generic[field];
                } else {
                    result[field] = generic[field];
                }
            }

            data.push(result);
        }

        return data;
    }

    /**
     * Liefert das Feld nach welchem sortiert werden soll
     */
    public getSortField(): string {
        let order = this.orderBy();
        let columns = this.columns();

        if (order != null && order.length > 0) {
            for (let i = 0; i < order.length; i++) {
                for (let j = 0; j < columns.length; j++) {
                    if (columns[j].field === order[i].field) {
                        return columns[j].name.replace(/\.ListViewValue/, '.Value');
                    }
                }
            }
        }

        return null;
    }

    /**
     * Gibt an, ob die Suchergebnise umgekehrt sortiert werden sollen
     */
    public getReverseSort(): boolean {
        let order = this.orderBy();
        let columns = this.columns();

        if (order != null && order.length > 0) {
            for (let i = 0; i < order.length; i++) {
                for (let j = 0; j < columns.length; j++) {
                    if (columns[j].field === order[i].field) {
                        return order[i].dir === 'DESC';
                    }
                }
            }
        }

        return false;
    }

    /**
    * @override
    */
    public onMouseOver(id: string, rowData: any, event?: JQuery.MouseOverEvent) {
        if (this.isDisposed)
            return;

        const delayedHover = () => {
            if (this.isDisposed) {
                //Wenn eine DV in der Anwendungssuche geöffnet wird, bevor Tooltip angezeigt wird, 
                //soll es sich hier als mouseOut verhalten und abgebrochen werden
                this.onMouseOut();
                return;
            }

            this.hovered({ ...rowData, target: event.target } as ISearchGridDTO);
        };

        //Wenn das Hover über ein Element weniger als 1000 ms gedauert hat,
        //soll das Element nicht als hovered gesetzt werden, damit kein Request für Tooltip an Server gesendet wird.
        this.timeoutId = window.setTimeout(delayedHover, 1000);
    }

    /**
    * @override
    */
    public onMouseOut() {
        clearTimeout(this.timeoutId);
    }

}

ComponentManager.setViewModel('SearchGridViewModel', SearchGridViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');